import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "reactstrap";

import { fetchStatisticsReport } from "../../ReduxToolkit/Reducers/ReportSlice";
import { RootState } from "../../ReduxToolkit/Store";
import {
  AsOn,
  DataHeads,
  getPreviousFinancialYearHeader,
  Nos,
  SBU,
  SNo,
} from "../../utils/Constant";
import { formatString } from "../../utils/helper/helper";

import "./style.scss";

interface TrainingDataTableProps {
  domain_id: string;
  financial_year: string;
}

const TrainingDataTable: React.FC<TrainingDataTableProps> = ({
  domain_id,
  financial_year,
}) => {
  const dispatch = useDispatch();
  const { statisticsReportData } = useSelector(
    (state: RootState) => state.report
  );

  useEffect(() => {
    if (domain_id && financial_year) {
      dispatch(fetchStatisticsReport({ domain_id, financial_year }) as any);
    }
  }, [dispatch, domain_id, financial_year]);

  const nextYear = parseInt(financial_year, 10) + 1;

  const fyShort = String(financial_year).slice(-2);
  const nextYearShort = String(nextYear).slice(-2);

  const headers = [
    {
      key: `Mar/${fyShort}`,
      value: getPreviousFinancialYearHeader(financial_year),
    },
    { key: `Apr/${fyShort}`, value: `${AsOn} (4/${fyShort})` },
    { key: `May/${fyShort}`, value: `${AsOn} (5/${fyShort})` },
    { key: `Jun/${fyShort}`, value: `${AsOn} (6/${fyShort})` },
    { key: `Jul/${fyShort}`, value: `${AsOn} (7/${fyShort})` },
    { key: `Aug/${fyShort}`, value: `${AsOn} (8/${fyShort})` },
    { key: `Sep/${fyShort}`, value: `${AsOn} (9/${fyShort})` },
    { key: `Oct/${fyShort}`, value: `${AsOn} (10/${fyShort})` },
    { key: `Nov/${fyShort}`, value: `${AsOn} (11/${fyShort})` },
    { key: `Dec/${fyShort}`, value: `${AsOn} (12/${fyShort})` },
    { key: `Jan/${nextYearShort}`, value: `${AsOn} (1/${nextYearShort})` },
    { key: `Feb/${nextYearShort}`, value: `${AsOn} (2/${nextYearShort})` },
    { key: `Mar/${nextYearShort}`, value: `${AsOn} (3/${nextYearShort})` },
  ];

  const sortedStatisticsReportData = statisticsReportData
    ? [...statisticsReportData].sort((a, b) => {
        if (a.sbu_name === "Overall") return -1;
        if (b.sbu_name === "Overall") return 1;
        return 0;
      })
    : [];

  const generateTableData = () => {
    const tableData: {
      sNo: string;
      sbu?: string;
      dataHead: string;
      isHeader?: boolean;
      key?: string;
      levelKey?: string;
      sbuKey?: string;
      isSubHeader?: boolean;
    }[] = [];

    let sbuCounter = 0;

    sortedStatisticsReportData.forEach(data => {
      const sbuSNo = `1.${sbuCounter}`;
      tableData.push({
        sNo: sbuSNo,
        sbu: data.sbu_name,
        dataHead: data.sbu_name === "overall" ? "-" : null,
        isHeader: true,
        key: "overall_certified_percentage",
        levelKey: "total",
        sbuKey: data.sbu_name,
      });

      let levelCounter = 1;

      data.levels.forEach(level => {
        const levelSNo = `${sbuSNo}.${levelCounter}`;
        tableData.push({
          sNo: levelSNo,
          dataHead: `${level.level} (${Nos})`,
          sbuKey: data.sbu_name,
          levelKey: level.level,
          key: "certified",
          isSubHeader: true,
        });

        Object.keys(level.metrics).forEach(metricKey => {
          if (metricKey !== "certified") {
            tableData.push({
              sNo: "",
              dataHead: formatString(metricKey),
              key: metricKey,
              levelKey: level.level,
              sbuKey: data.sbu_name,
            });
          }
        });
        levelCounter++;
      });
      sbuCounter++;
    });
    return tableData;
  };

  const tableData = generateTableData();
  const getMonthData = (sbu, monthKey, levelKey, key, isHeader) => {
    if (isHeader) {
      const value = statisticsReportData.find(item => item.sbu_name === sbu)?.[
        key
      ]?.[monthKey];

      return value !== undefined ? value : "-";
    }
    const value = statisticsReportData
      .find(item => item.sbu_name === sbu)
      ?.levels.find(level => level.level === levelKey)?.metrics?.[key]?.[
      monthKey
    ];

    return value !== undefined ? value : "-";
  };

  return (
    <div className="table-container">
      <Table className="training-table">
        <thead>
          <tr>
            <th rowSpan={2}>{SNo}</th>
            <th rowSpan={2}>{SBU}</th>
            <th rowSpan={2}>{DataHeads}</th>
            {headers.map((header, index) => (
              <th key={index}>{header.value}</th>
            ))}
          </tr>
        </thead>

        <tbody>
          {tableData.map((row, index) => (
            <tr key={index} className={row.isHeader ? "table-header" : ""}>
              <td>{row.sNo}</td>
              <td className={row.sbu ? "highlighted fw-bold" : ""}>
                {row.sbu || ""}
              </td>
              <td className={row.isSubHeader ? "fw-bold" : ""}>
                {row.dataHead}
              </td>
              {headers.map((header, i) => (
                <td key={i}>
                  {getMonthData(
                    row.sbuKey,
                    header.key,
                    row.levelKey,
                    row.key,
                    row.isHeader
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default TrainingDataTable;
